/**
 * Alert Sound Service — Web Audio API
 *
 * Generates a loud, looping Uber/Lyft-style ring for incoming ride requests.
 * Uses an urgent two-phase ringtone pattern that repeats continuously.
 * Works on Chrome, Safari, Firefox, and mobile browsers.
 * No external audio file required.
 *
 * IMPORTANT: Call initAudio() during a user gesture (e.g. button press)
 * BEFORE using startAlert(). Chrome/Safari require a user gesture to
 * create and resume an AudioContext.
 */

let audioCtx = null;
let loopTimer = null;
let isPlaying = false;

function getContext() {
  if (!audioCtx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) {
        console.warn('[AlertSound] Web Audio API not supported');
        return null;
      }
      audioCtx = new AC();
      console.log('[AlertSound] AudioContext created, state:', audioCtx.state);
    } catch (e) {
      console.error('[AlertSound] Failed to create AudioContext:', e);
      return null;
    }
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().then(() => {
      console.log('[AlertSound] AudioContext resumed successfully');
    }).catch((e) => {
      console.warn('[AlertSound] AudioContext resume failed:', e);
    });
  }
  return audioCtx;
}

/**
 * Initialize the AudioContext. MUST be called during a user gesture
 * (e.g. click/tap handler) to satisfy browser autoplay policies.
 * Safe to call multiple times — idempotent.
 */
export function initAudio() {
  console.log('[AlertSound] initAudio called (user gesture context)');
  const ctx = getContext();
  if (ctx) {
    // Force resume — this works because we're in a user gesture handler
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        console.log('[AlertSound] initAudio: AudioContext resumed, state:', ctx.state);
      }).catch((e) => {
        console.warn('[AlertSound] initAudio: resume failed:', e);
      });
    } else {
      console.log('[AlertSound] initAudio: AudioContext already', ctx.state);
    }
  }
}

/**
 * Play a single urgent ring pattern (~1.2 seconds).
 * Two rapid ascending tones followed by a higher confirmation tone, repeated.
 */
function playRing() {
  const ctx = getContext();
  if (!ctx) {
    console.warn('[AlertSound] playRing: no AudioContext available');
    return;
  }

  if (ctx.state !== 'running') {
    console.warn('[AlertSound] playRing: AudioContext state is', ctx.state, '(need "running")');
    // Try to resume — may work if we've already had a user gesture
    ctx.resume();
    return;
  }

  const now = ctx.currentTime;
  const volume = 0.5;

  // Ring tone 1 — 784 Hz (G5)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.value = 784;
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(volume, now + 0.03);
  gain1.gain.setValueAtTime(volume, now + 0.15);
  gain1.gain.linearRampToValueAtTime(0, now + 0.20);
  osc1.connect(gain1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.25);

  // Ring tone 2 — 988 Hz (B5) — quick follow
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.value = 988;
  gain2.gain.setValueAtTime(0, now + 0.12);
  gain2.gain.linearRampToValueAtTime(volume, now + 0.15);
  gain2.gain.setValueAtTime(volume, now + 0.30);
  gain2.gain.linearRampToValueAtTime(0, now + 0.35);
  osc2.connect(gain2).connect(ctx.destination);
  osc2.start(now + 0.12);
  osc2.stop(now + 0.40);

  // Ring tone 3 — 1175 Hz (D6) — high confirmation
  const osc3 = ctx.createOscillator();
  const gain3 = ctx.createGain();
  osc3.type = 'sine';
  osc3.frequency.value = 1175;
  gain3.gain.setValueAtTime(0, now + 0.25);
  gain3.gain.linearRampToValueAtTime(volume * 0.85, now + 0.28);
  gain3.gain.setValueAtTime(volume * 0.85, now + 0.50);
  gain3.gain.linearRampToValueAtTime(0, now + 0.60);
  osc3.connect(gain3).connect(ctx.destination);
  osc3.start(now + 0.25);
  osc3.stop(now + 0.65);

  // Second hit — repeat for urgency (offset by 0.7s)
  const osc4 = ctx.createOscillator();
  const gain4 = ctx.createGain();
  osc4.type = 'sine';
  osc4.frequency.value = 784;
  gain4.gain.setValueAtTime(0, now + 0.70);
  gain4.gain.linearRampToValueAtTime(volume, now + 0.73);
  gain4.gain.setValueAtTime(volume, now + 0.85);
  gain4.gain.linearRampToValueAtTime(0, now + 0.90);
  osc4.connect(gain4).connect(ctx.destination);
  osc4.start(now + 0.70);
  osc4.stop(now + 0.95);

  const osc5 = ctx.createOscillator();
  const gain5 = ctx.createGain();
  osc5.type = 'sine';
  osc5.frequency.value = 1175;
  gain5.gain.setValueAtTime(0, now + 0.82);
  gain5.gain.linearRampToValueAtTime(volume * 0.85, now + 0.85);
  gain5.gain.setValueAtTime(volume * 0.85, now + 1.05);
  gain5.gain.linearRampToValueAtTime(0, now + 1.15);
  osc5.connect(gain5).connect(ctx.destination);
  osc5.start(now + 0.82);
  osc5.stop(now + 1.20);
}

/**
 * Start the looping alert. Plays ring pattern, repeats every 1.8 seconds.
 */
export function startAlert() {
  if (isPlaying) return;
  isPlaying = true;
  console.log('[AlertSound] startAlert: beginning loop');

  // Play immediately
  playRing();

  // Vibrate if supported (mobile)
  if (navigator.vibrate) {
    navigator.vibrate([300, 150, 300, 150, 300]);
  }

  // Loop: ring every 1.8 seconds for urgency
  loopTimer = setInterval(() => {
    playRing();
    if (navigator.vibrate) {
      navigator.vibrate([300, 150, 300]);
    }
  }, 1800);
}

/**
 * Stop the looping alert immediately.
 */
export function stopAlert() {
  if (isPlaying) {
    console.log('[AlertSound] stopAlert: stopping loop');
  }
  isPlaying = false;
  if (loopTimer) {
    clearInterval(loopTimer);
    loopTimer = null;
  }
  if (navigator.vibrate) {
    navigator.vibrate(0);
  }
}

/**
 * Returns true if the alert is currently playing.
 */
export function isAlertPlaying() {
  return isPlaying;
}
